import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { ApiRouteConstants } from '../../../../../core/constants/api-routes';
import { SUPPRESS_NETWORK_ERRORS } from '../../../../../core/constants/http-context-tokens';
import { ApplicationState } from '../../../../../core/models/application-state/application-state';
import { GridApplicationRowData } from '../../../../dashboard/models/grid-application-row-data';
import { CheckSafeHarborRequest } from '../../../direct-entry/schedules-d-and-h/components/safe-harbor-results/models/check-safe-harbor-request';
import { CheckSafeHarborResponse } from '../../../direct-entry/schedules-d-and-h/components/safe-harbor-results/models/check-safe-harbor-response';
import { DirectEntryStepEnum } from '../../../direct-entry/shared/constants/direct-entry-steps';
import { ApplicationSubmitToPscaResponse } from '../../../direct-entry/shared/models/application-submit-to-psca-response';
import { EwaSubmissionResponse } from '../../../direct-entry/submit-to-ewa/components/shared/models/ewa-submission-response';
import { SubmitToEwaDetailsResponse } from '../../../direct-entry/submit-to-ewa/components/shared/models/submit-to-ewa-details-response';
import { SubmitToEwaRequestBase } from '../../../direct-entry/submit-to-ewa/components/shared/models/submit-to-ewa-request';
import { ApplicationCreationRequest } from '../../models/requests/application-creation-request';
import { SubmitToFiveDayHoldRequest } from '../../models/requests/submit-to-five-day-hold-request';
import { ApplicationAdvisorViewStatusResponse } from '../../models/responses/application-advisor-view-status-response';
import { ApplicationCreationResponse } from '../../models/responses/application-creation-response';
import { ApplicationSubmitToFccResponse } from '../../models/responses/application-submit-to-fcc-response';
import { SubmitToFiveDayHoldResponse } from '../../models/responses/submit-to-five-day-hold-response';
import { ApplicationStateService } from '../application-state/application-state.service';
import { ApplicationSubmitToFccRequest } from '../../models/requests/application-submit-to-fcc-request';

@Injectable({
    providedIn: 'root',
})
export class ApplicationService {
    private readonly applicationApiRoute = ApiRouteConstants.Application;
    private readonly http = inject(HttpClient);
    private readonly applicationStateService = inject(ApplicationStateService);

    createApplication(
        request: ApplicationCreationRequest,
    ): Observable<ApplicationCreationResponse> {
        return this.http.post<ApplicationCreationResponse>(
            this.applicationApiRoute.rootUrl,
            request,
            { context: new HttpContext().set(SUPPRESS_NETWORK_ERRORS, true) },
        );
    }

    getAllForDashboard() {
        return this.http.get<GridApplicationRowData[]>(
            this.applicationApiRoute.rootUrl,
        );
    }

    /**
     * Deletes an application by its ID.
     *
     * @param id The ID of the application to delete.
     */
    delete(applicationId: string): Observable<void> {
        return this.http.delete<void>(
            this.applicationApiRoute.getRootUrlWithApplicationId(applicationId),
        );
    }

    /**
     * Retrieving the information needed to display the final submission page.
     *
     * @param applicationId Application ID
     * @returns Observable of SubmitToEwaDetailsResponse with the data needed for the final submission page.
     */
    getDataForFinalSubmission(
        applicationId: string,
    ): Observable<SubmitToEwaDetailsResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'submitToEwa',
                applicationId,
            );
        return this.http.get<SubmitToEwaDetailsResponse>(fullRoute);
    }

    /**
     * Makes request to API to update the submission as final and considers the application as submitted to EWA.
     *
     * @param request Request object containing the application ID and the final submission data.
     * @returns A null observable since the final submission does not contain any stored validations.
     */
    finalSubmissionToEwa<T extends SubmitToEwaRequestBase>(
        request: T,
    ): Observable<EwaSubmissionResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'submitToEwa',
                request.applicationId,
            );

        return this.http.post<EwaSubmissionResponse>(fullRoute, request);
    }

    /**
     * Verifies if all the steps (excluding the final step) are successfully passing for the application.
     *
     * @param applicationId
     * @returns
     */
    getAllStepsValid(applicationId: string): Observable<boolean> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'allStepsValid',
                applicationId,
            );
        return this.http.get<boolean>(fullRoute);
    }

    /**
     * Fetches the current application state from the API based on the provided ID.
     * Once fetched, it will update the application state service with the new state.
     */
    refreshApplicationStateAsync(applicationId: string): void {
        this.getApplicationState(applicationId).subscribe();
    }

    /**
     * Gets the application state from API and updates the application state service.
     *
     * @param applicationId
     * @returns
     */
    getApplicationState(applicationId: string): Observable<ApplicationState> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'state',
                applicationId,
            );

        return this.http.get<ApplicationState>(fullRoute).pipe(
            tap((applicationState) => {
                this.applicationStateService.setApplicationState(
                    applicationState,
                );
            }),
        );
    }

    /**
     * Gets the advisor view status for the application to determine if the advisor has viewed the application or not.
     *
     * @param applicationId The ID of the application to get the advisor view status for.
     * @returns The advisor view status for the application.
     */
    getApplicationAdvisorViewStatus(
        applicationId: string,
    ): Observable<ApplicationAdvisorViewStatusResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'advisorViewStatus',
                applicationId,
            );

        return this.http.get<ApplicationAdvisorViewStatusResponse>(fullRoute);
    }

    /**
     * Marks the application as viewed by the advisor.
     *
     * @param applicationId The ID of the application to mark as viewed by the advisor.
     * @returns The updated advisor view status for the application.
     */
    markApplicationAsViewedByAdvisor(
        applicationId: string,
    ): Observable<ApplicationAdvisorViewStatusResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'advisorViewStatus',
                applicationId,
            );

        return this.http.post<ApplicationAdvisorViewStatusResponse>(
            fullRoute,
            null,
        );
    }

    /**
     * Gets the applicable Direct Entry steps based on the application's purpose from the API.
     *
     * @param applicationId The ID of the application to get the Direct Entry steps for.
     * @returns The Direct Entry steps for the application.
     */
    getDirectEntrySteps(
        applicationId: string,
    ): Observable<DirectEntryStepEnum[]> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'directEntrySteps',
                applicationId,
            );

        return this.http.get<DirectEntryStepEnum[]>(fullRoute);
    }

    checkSafeHarbor(
        request: CheckSafeHarborRequest,
    ): Observable<CheckSafeHarborResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'safeHarbor',
                request.applicationId,
            );

        return this.http.post<CheckSafeHarborResponse>(fullRoute, request);
    }

    submitToFcc(
        applicationId: string,
        skipFrequencySelectionValidation?: boolean,
    ): Observable<ApplicationSubmitToFccResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'submitToFcc',
                applicationId,
            );

        const request: ApplicationSubmitToFccRequest = { applicationId };

        if (
            skipFrequencySelectionValidation !== null &&
            skipFrequencySelectionValidation !== undefined
        ) {
            request.skipFrequencySelectionValidation =
                skipFrequencySelectionValidation;
        }

        return this.http.post<ApplicationSubmitToFccResponse>(
            fullRoute,
            request,
        );
    }

    submitToFiveDayHold(
        applicationId: string,
        request: SubmitToFiveDayHoldRequest,
    ): Observable<SubmitToFiveDayHoldResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'submitToFiveDayHold',
                applicationId,
            );

        return this.http.post<SubmitToFiveDayHoldResponse>(fullRoute, request);
    }

    submitToPsca(
        applicationId: string,
    ): Observable<ApplicationSubmitToPscaResponse> {
        const fullRoute =
            this.applicationApiRoute.getFullRouteWithApplicationId(
                'submitToPsca',
                applicationId,
            );

        return this.http.post<ApplicationSubmitToPscaResponse>(fullRoute, null);
    }
}
